(function () {
  if (window.CraftCellarsSectionFilters?.initialized) return;

  const SCOPE_SELECTOR = '[data-location-filter-section]';
  let resizeFrame = null;

  // Theme setting ON + Location modal ON. Avoids hiding from stale localStorage when modal is off.
  function isProductHidingEnabled() {
    const config = window.craftCellarsLocationConfig;
    return config?.productHidingEnabled === true && config?.enabled === true;
  }

  // Reads the shopper's current location so every managed section stays in sync.
  // Uses CraftCellarsLocation only — no direct localStorage — so when Modal + PDP
  // persistence is off, stale stored values cannot hide products.
  function getStoredSelection() {
    return window.CraftCellarsLocation?.getSelection?.() || null;
  }

  // Resolves the active location key used to decide which products stay visible.
  // Without a persisted selection, do not fall back to default when persistence is off.
  function getSelectionKey(selection) {
    if (!isProductHidingEnabled()) return '';

    if (selection?.key) return selection.key;

    if (window.CraftCellarsLocation?.isPersistenceEnabled?.() === false) {
      return '';
    }

    return window.craftCellarsLocationConfig?.defaultLocationKey || '';
  }

  // Identifies products that are controlled by the shared location filter engine.
  function isManagedItem(node) {
    return node?.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-location-filter-item');
  }

  // Applies the location-specific stockout rule for the current shopper selection.
  function shouldHideItem(item, selectionKey) {
    if (!isProductHidingEnabled()) return false;

    if (selectionKey === 'location_1') {
      return item.dataset.mainStoreStockout === 'true';
    }

    if (selectionKey === 'location_2') {
      return item.dataset.northlandStockout === 'true';
    }

    return false;
  }

  class LocationFilterScope {
    constructor(scope) {
      this.scope = scope;
      this.layout = scope.dataset.locationFilterLayout || 'grid';
      this.maxVisible = Number(scope.dataset.locationFilterMaxVisible || 0);
      this.visibleIncrement = Number(scope.dataset.locationFilterVisibleIncrement || 0);
      this.currentVisibleLimit = this.maxVisible;
      this.wrapper =
        scope.querySelector('[data-location-filter-items]') ||
        (scope.hasAttribute('data-location-filter-items') ? scope : null);
      this.sliderHost =
        scope.querySelector('[data-location-filter-slider]') ||
        (scope.hasAttribute('data-location-filter-slider') ? scope : null);
      this.nav = scope.querySelector('[data-location-filter-nav]');
      this.prev = scope.querySelector('.swiper-button-prev');
      this.next = scope.querySelector('.swiper-button-next');
      this.pagination = scope.querySelector('.swiper-pagination');
      this.originalItems = [];
      this.originalStaticItems = [];
      this.isApplying = false;
      this.suppressObserver = false;

      this.refreshItems();
      this.observeWrapper();
    }

    // Returns only the items that can be shown for the current location.
    getValidItems(selectionKey) {
      return this.originalItems.filter(
        item =>
          !item.hasAttribute('data-location-filter-static') && !shouldHideItem(item, selectionKey)
      );
    }

    // Caches the original product and static nodes so filtering can rebuild safely.
    refreshItems() {
      if (!this.wrapper) return;

      Array.from(this.wrapper.children)
        .filter(isManagedItem)
        .forEach(item => {
          if (!this.originalItems.includes(item)) {
            this.originalItems.push(item);
          }
        });

      Array.from(this.wrapper.children)
        .filter(
          item =>
            item?.nodeType === Node.ELEMENT_NODE && item.hasAttribute('data-location-filter-static')
        )
        .forEach(item => {
          if (!this.originalStaticItems.includes(item)) {
            this.originalStaticItems.push(item);
          }
        });
    }

    // Reapplies filtering when new managed items are appended, such as load-more content.
    observeWrapper() {
      if (!this.wrapper || typeof MutationObserver !== 'function') return;

      this.observer = new MutationObserver(mutations => {
        if (this.isApplying || this.suppressObserver) return;

        const hasManagedAdditions = mutations.some(mutation =>
          Array.from(mutation.addedNodes).some(node => isManagedItem(node))
        );

        if (!hasManagedAdditions) return;

        this.refreshItems();
        this.apply(getStoredSelection());
      });

      this.observer.observe(this.wrapper, { childList: true });
    }

    // Returns the slider instance so navigation can stay accurate after filtering.
    getSlider() {
      return this.sliderHost?.slider || null;
    }

    // Reads the current slider layout so navigation only appears when needed.
    getSlidesPerView() {
      const slider = this.getSlider();
      if (!slider) return Number.POSITIVE_INFINITY;

      const rawSlidesPerView = slider.params?.slidesPerView;
      const parsedSlidesPerView =
        typeof rawSlidesPerView === 'number'
          ? rawSlidesPerView
          : Number.parseFloat(rawSlidesPerView || '1');

      return Number.isFinite(parsedSlidesPerView) && parsedSlidesPerView > 0
        ? parsedSlidesPerView
        : 1;
    }

    // Returns the visible products after location rules and section limits are applied.
    getVisibleItems(selectionKey) {
      const validItems = this.getValidItems(selectionKey);

      const activeVisibleLimit =
        this.currentVisibleLimit > 0 ? this.currentVisibleLimit : this.maxVisible;
      const shouldLimitItems = activeVisibleLimit > 0;

      if (!shouldLimitItems) {
        return validItems;
      }

      return validItems.slice(0, activeVisibleLimit);
    }

    // Increases the visible window while preserving location filtering.
    revealMore(count = this.visibleIncrement) {
      if (!count || count < 1) return;

      this.refreshItems();
      const selection = getStoredSelection();
      const selectionKey = getSelectionKey(selection);
      const previousVisibleCount = this.getVisibleItems(selectionKey).length;
      const baseline = this.currentVisibleLimit > 0 ? this.currentVisibleLimit : this.maxVisible;
      this.currentVisibleLimit = baseline + count;
      this.apply(selection);

      const nextVisibleCount = this.getVisibleItems(selectionKey).length;
      const remainingHiddenCount = this.getValidItems(selectionKey).length - nextVisibleCount;

      return {
        revealedCount: Math.max(0, nextVisibleCount - previousVisibleCount),
        hasMoreHidden: remainingHiddenCount > 0,
      };
    }

    // Keeps static inserts visible only when they still make sense for the location.
    getVisibleStaticItems(selectionKey) {
      return this.originalStaticItems.filter(item => !shouldHideItem(item, selectionKey));
    }

    // Rebuilds grid output so products and static inserts keep their intended order.
    buildGridNodes(selectionKey) {
      const visibleItems = this.getVisibleItems(selectionKey);
      const staticItems = this.getVisibleStaticItems(selectionKey);
      const remainingStaticItems = staticItems.slice();
      const nodes = [];

      const flushStaticItems = (visibleCount, flushAll = false) => {
        while (remainingStaticItems.length) {
          const nextStaticItem = remainingStaticItems[0];
          const insertAfter = Number(nextStaticItem.dataset.locationFilterInsertAfter || 0);

          if (!flushAll && insertAfter > visibleCount) {
            break;
          }

          nodes.push(nextStaticItem);
          remainingStaticItems.shift();
        }
      };

      flushStaticItems(0);

      visibleItems.forEach((item, index) => {
        nodes.push(item);
        flushStaticItems(index + 1);
      });

      flushStaticItems(Number.POSITIVE_INFINITY, true);

      return {
        nodes,
        visibleCount: visibleItems.length,
      };
    }

    // Shows or hides slider controls based on how many products remain visible.
    updateNavigation(visibleCount) {
      if (!this.nav && !this.prev && !this.next && !this.pagination) return;

      const shouldShowNavigation =
        this.layout === 'slider' && visibleCount > this.getSlidesPerView();

      if (this.nav) this.nav.hidden = !shouldShowNavigation;
      if (this.pagination) this.pagination.hidden = !shouldShowNavigation;
      if (this.prev) this.prev.hidden = !shouldShowNavigation;
      if (this.next) this.next.hidden = !shouldShowNavigation;
    }

    // Refreshes the slider after filtering so there are no blank or broken slides.
    refreshSlider(visibleCount) {
      const slider = this.getSlider();
      if (!slider) return;

      slider.allowSlideNext = true;
      slider.allowSlidePrev = true;
      slider.allowTouchMove = true;

      if (typeof slider.updateSlides === 'function') slider.updateSlides();
      if (typeof slider.updateSlidesClasses === 'function') slider.updateSlidesClasses();
      if (typeof slider.updateProgress === 'function') slider.updateProgress();
      if (typeof slider.update === 'function') slider.update();
      if (typeof slider.slideTo === 'function') slider.slideTo(0, 0, false);

      if (visibleCount <= this.getSlidesPerView()) {
        slider.allowSlideNext = false;
        slider.allowSlidePrev = false;
        slider.allowTouchMove = false;
      }
    }

    // Replaces the grid with only the products and inserts that should currently appear.
    applyGrid(selectionKey) {
      if (!this.wrapper) return;

      const { nodes, visibleCount } = this.buildGridNodes(selectionKey);

      this.isApplying = true;
      this.suppressObserver = true;

      try {
        this.wrapper.replaceChildren(...nodes);
        this.updateNavigation(visibleCount);
      } finally {
        this.isApplying = false;
        queueMicrotask(() => {
          this.suppressObserver = false;
        });
      }
    }

    // Replaces the slider with only the location-valid slides and refreshes controls.
    applySlider(selectionKey) {
      if (!this.wrapper) return;

      const visibleItems = this.getVisibleItems(selectionKey);

      this.isApplying = true;
      this.suppressObserver = true;

      try {
        this.wrapper.replaceChildren(...visibleItems);
        this.updateNavigation(visibleItems.length);
        this.refreshSlider(visibleItems.length);
      } finally {
        this.isApplying = false;
        queueMicrotask(() => {
          this.suppressObserver = false;
        });
      }
    }

    // Rebuilds the scope from the original item set for the active location.
    apply(selection) {
      if (!this.wrapper) return;

      this.refreshItems();

      const selectionKey = getSelectionKey(selection);

      if (this.layout === 'slider') {
        this.applySlider(selectionKey);
        return;
      }

      this.applyGrid(selectionKey);
    }
  }

  const manager = {
    initialized: true,
    scopes: new Map(),

    // Registers every eligible section and applies the current shopper location to it.
    init(root = document) {
      const scopeElements = root.matches?.(SCOPE_SELECTOR)
        ? [root]
        : Array.from(root.querySelectorAll(SCOPE_SELECTOR));

      scopeElements.forEach(scope => {
        if (this.scopes.has(scope)) return;

        this.scopes.set(scope, new LocationFilterScope(scope));
      });

      this.applyAll(getStoredSelection());
    },

    // Reapplies location filtering across all registered sections at once.
    applyAll(selection) {
      Array.from(this.scopes.entries()).forEach(([scope, instance]) => {
        if (!scope.isConnected) {
          this.scopes.delete(scope);
          return;
        }

        instance.apply(selection);
      });
    },

    // Exposes per-scope reveal state for load-more buttons that need it.
    revealMore(scopeOrElement, count) {
      if (!scopeOrElement) return;

      const scope = scopeOrElement.matches?.(SCOPE_SELECTOR)
        ? scopeOrElement
        : scopeOrElement.closest?.(SCOPE_SELECTOR);

      if (!scope) return;

      const instance = this.scopes.get(scope);
      if (!instance) return;

      return instance.revealMore(count);
    },
  };

  window.CraftCellarsSectionFilters = manager;

  document.addEventListener('craft-cellars:location-selected', event => {
    manager.applyAll(event.detail || getStoredSelection());
  });

  document.addEventListener('DOMContentLoaded', () => manager.init());
  document.addEventListener('shopify:section:load', event => manager.init(event.target));

  window.addEventListener('resize', () => {
    if (resizeFrame) return;

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      manager.applyAll(getStoredSelection());
    });
  });

  if (document.readyState !== 'loading') {
    manager.init();
  }
})();
